import {Grid, GridCellProps, GridColumn, GridHeaderCellProps} from "@progress/kendo-react-grid"
import {Tooltip} from "@progress/kendo-react-tooltip"
import {MDBBreadcrumb, MDBBreadcrumbItem, MDBIcon} from "mdbreact"
import {useEffect, useRef, useState} from "react"
import {RootState} from "../../rootReducer"
import {SpinnerGrid} from "../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../store"
import {getAccessPolicies} from "./accessPoliciesSlice"

const AccessPolicies = () => {
    const dispatch = useAppDispatch()
    const allPermissions = useAppSelector((state: RootState) => state.containers.accessPolicies.permissions)
    const roles = useAppSelector((state: RootState) => state.containers.users.roles)
    const loadingPolicies = useAppSelector((state: RootState) => state.containers.accessPolicies.loading)
    const gridRef = useRef<any>(null)
    const [descriptionColWidth, setDescriptionColWidth] = useState<number | undefined>(
        window.innerWidth > 1600 ? undefined : 650,
    )

    const handleResize = () => {
        setDescriptionColWidth(window.innerWidth > 1600 ? undefined : 650)
    }

    useEffect(() => {
        if (descriptionColWidth === undefined) {
            gridRef.current.headerRef.current.table.style.removeProperty("width")
            gridRef.current.tableElement.style.removeProperty("width")
        }
    }, [descriptionColWidth])

    useEffect(() => {
        window.addEventListener("resize", handleResize)
        dispatch(getAccessPolicies())

        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, [])

    const csrbasic_permissions = allPermissions.find(p => p.role === roles?.[0].id)?.operation ?? null
    const csradvanced_permissions = allPermissions.find(p => p.role === roles?.[1].id)?.operation ?? null
    const regional_permissions = allPermissions.find(p => p.role === roles?.[2].id)?.operation ?? null
    const global_permissions = allPermissions.find(p => p.role === roles?.[3].id)?.operation ?? null

    let csr_basic_permissions: any[] = []
    let csr_advanced_permissions: any[] = []
    let regional_admin_permissions: any[] = []
    let global_admin_permissions: any[] = []

    csrbasic_permissions?.forEach((element, i) => {
        var object = {
            Name: element.section,
            Description: element.description,
            CSRBasic: {
                ADD: element.add,
                EDIT: element.edit,
                DELETE: element.delete,
            },
        }

        csr_basic_permissions.push(object)
    })
    csradvanced_permissions?.forEach((element, i) => {
        var perm = {...csr_basic_permissions}
        var object = {
            Name: perm[i].Name,
            Description: perm[i].Description,
            CSRBasic: {...perm[i].CSRBasic},
            CSRAdvanced: {
                ADD: element.add,
                EDIT: element.edit,
                DELETE: element.delete,
            },
        }
        csr_advanced_permissions.push(object)
    })
    regional_permissions?.forEach((element, i) => {
        var perm = {...csr_advanced_permissions}
        var object = {
            Name: perm[i].Name,
            Description: perm[i].Description,
            CSRBasic: {...perm[i].CSRBasic},
            CSRAdvanced: {...perm[i].CSRAdvanced},
            RegionalAdmin: {
                ADD: element.add,
                EDIT: element.edit,
                DELETE: element.delete,
            },
        }
        regional_admin_permissions.push(object)
    })
    global_permissions?.forEach((element, i) => {
        var perm = {...regional_admin_permissions}
        var object = {
            Name: perm[i].Name,
            Description: perm[i].Description,
            CSRBasic: {...perm[i].CSRBasic},
            CSRAdvanced: {...perm[i].CSRAdvanced},
            RegionalAdmin: {...perm[i].RegionalAdmin},
            GlobalAdmin: {
                ADD: element.add,
                EDIT: element.edit,
                DELETE: element.delete,
            },
        }
        global_admin_permissions.push(object)
    })

    const DescriptionCell = (props: GridCellProps) => {
        //return <td title={props.dataItem[`${props.field}`]}>{props.dataItem[`${props.field}`]}</td>
        return (
            <div className="m-2">
                <p className="font-weight-bold text-muted mb-0">{props.dataItem["Name"]}</p>
                <span className="small">{props.dataItem["Description"]}</span>
            </div>
        )
    }

    const OperationCell = (props: GridCellProps) => {
        let roleName = `${props.field}`.split(".")[0]
        let roleOperation = `${props.field}`.split(".")[1]
        let operations = props.dataItem[roleName][roleOperation]
        return (
            <td className="text-center">
                {operations ? (
                    <MDBIcon color="success" fas icon="check" className="accesscheck" />
                ) : operations !== null ? (
                    <MDBIcon color="danger" fas icon="times" className="accesscheck" />
                ) : (
                    "-"
                )}
            </td>
        )
    }

    const HeaderCell = (props: any) => (
        <div className="text-center ">
            <span>{props.title}</span>
        </div>
    )
    const RoleNameHeaderCell = (props: any) => (
        <div className="text-center ">
            <span></span>
        </div>
    )

    const CSRBasicHeaderCell = (props: GridHeaderCellProps) => {
        const headertitle = roles?.[0].displayName
        return (
            <div className="text-center">
                <span>{headertitle}</span>
            </div>
        )
    }

    const CSRAdvancedHeaderCell = (props: GridHeaderCellProps) => {
        const headertitle = roles?.[1].displayName
        return (
            <div className="text-center">
                <span>{headertitle}</span>
            </div>
        )
    }

    const RegionalHeaderCell = (props: GridHeaderCellProps) => {
        const headertitle = roles?.[2].displayName
        return (
            <div className="text-center">
                <span>{headertitle}</span>
            </div>
        )
    }

    const GlobalHeaderCell = (props: GridHeaderCellProps) => {
        const headertitle = roles?.[3].displayName
        return (
            <div className="text-center">
                <span>{headertitle}</span>
            </div>
        )
    }

    return (
        <>
            <MDBBreadcrumb>
                <MDBBreadcrumbItem active>Access Policies</MDBBreadcrumbItem>
            </MDBBreadcrumb>
            <div className="position-relative">
                {loadingPolicies && <SpinnerGrid />}

                {global_admin_permissions && (
                    <div className="accesspolicy-grid">
                        <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                            <Grid
                                ref={gridRef}
                                style={{
                                    height: "calc(100vh - 10.30rem)",
                                }}
                                data={global_admin_permissions}
                            >
                                <GridColumn
                                    cell={DescriptionCell}
                                    headerCell={RoleNameHeaderCell}
                                    width={descriptionColWidth}
                                />

                                <GridColumn title="CSR Basic" headerCell={CSRBasicHeaderCell}>
                                    <GridColumn
                                        field="CSRBasic.ADD"
                                        title="Add"
                                        className="text-center"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="CSRBasic.EDIT"
                                        title="Edit"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="CSRBasic.DELETE"
                                        title="Del"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                </GridColumn>
                                <GridColumn title="CSR Advanced" headerCell={CSRAdvancedHeaderCell}>
                                    <GridColumn
                                        field="CSRAdvanced.ADD"
                                        title="Add"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="CSRAdvanced.EDIT"
                                        title="Edit"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="CSRAdvanced.DELETE"
                                        title="Del"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                </GridColumn>
                                <GridColumn title="Regional Admin" headerCell={RegionalHeaderCell}>
                                    <GridColumn
                                        field="RegionalAdmin.ADD"
                                        title="Add"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="RegionalAdmin.EDIT"
                                        title="Edit"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="RegionalAdmin.DELETE"
                                        title="Del"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                </GridColumn>
                                <GridColumn title="Global Administrator" headerCell={GlobalHeaderCell}>
                                    <GridColumn
                                        field="GlobalAdmin.ADD"
                                        title="Add"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="GlobalAdmin.EDIT"
                                        title="Edit"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                    <GridColumn
                                        field="GlobalAdmin.DELETE"
                                        title="Del"
                                        cell={OperationCell}
                                        headerCell={HeaderCell}
                                        width={70}
                                    />
                                </GridColumn>
                            </Grid>
                        </Tooltip>
                    </div>
                )}
            </div>
        </>
    )
}

export default AccessPolicies
